import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface TokenRefreshOptions {
  refreshThreshold?: number; // Minutes before expiry to refresh
  activityTimeout?: number; // Minutes of inactivity before warning
  sessionTimeout?: number; // Minutes of total inactivity before logout
  onTokenRefreshed?: (newToken: string) => void;
  onSessionWarning?: (minutesLeft: number) => void;
  onSessionExpired?: () => void;
}

export const useTokenRefresh = (options: TokenRefreshOptions = {}) => {
  const {
    refreshThreshold = 15, // Refresh 15 minutes before expiry
    activityTimeout = 25, // Warn after 25 minutes of inactivity
    sessionTimeout = 30, // Logout after 30 minutes of inactivity
    onTokenRefreshed,
    onSessionWarning,
    onSessionExpired
  } = options;

  const navigate = useNavigate();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);

  // Parse JWT token to get expiry
  const parseJwtToken = useCallback((token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        exp: payload.exp * 1000, // Convert to milliseconds
        iat: payload.iat * 1000,
        userId: payload.id || payload.user_id,
        email: payload.email
      };
    } catch (error) {
      console.error('❌ [TOKEN REFRESH] Failed to parse JWT:', error);
      return null;
    }
  }, []);

  // Refresh token via API
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      console.log('🔄 [TOKEN REFRESH] Attempting token refresh...');
      
      const currentToken = localStorage.getItem('auth_token');
      if (!currentToken) {
        console.warn('⚠️ [TOKEN REFRESH] No current token found');
        return false;
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const newToken = data.data?.accessToken;
        
        if (newToken) {
          localStorage.setItem('auth_token', newToken);
          console.log('✅ [TOKEN REFRESH] Token refreshed successfully');
          onTokenRefreshed?.(newToken);
          return true;
        }
      } else if (response.status === 401) {
        console.warn('⚠️ [TOKEN REFRESH] Refresh token expired or invalid');
        handleSessionExpired();
        return false;
      }
    } catch (error) {
      console.error('❌ [TOKEN REFRESH] Token refresh failed:', error);
    }
    return false;
  }, [onTokenRefreshed]);

  // Handle session expiry
  const handleSessionExpired = useCallback(() => {
    console.log('🚪 [SESSION] Session expired, logging out...');
    
    // Clear all auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('user');
    
    // Clear intervals
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    
    // Clear any existing toasts
    toast.dismiss();
    
    onSessionExpired?.();
    toast.error('Your session has expired. Please log in again.');
    navigate('/sign-in');
  }, [navigate, onSessionExpired]);

  // Handle session warning
  const handleSessionWarning = useCallback(() => {
    if (warningShownRef.current) return;
    
    warningShownRef.current = true;
    const minutesLeft = Math.ceil((sessionTimeout * 60 * 1000 - (Date.now() - lastActivityRef.current)) / (60 * 1000));
    
    console.log(`⚠️ [SESSION] Warning: ${minutesLeft} minutes until session timeout`);
    onSessionWarning?.(minutesLeft);
    
    toast(
      `Your session will expire in ${minutesLeft} minutes due to inactivity. Click anywhere to stay logged in.`,
      {
        duration: Infinity, // Don't auto-close
        style: {
          background: '#f59e0b',
          color: 'white',
        },
        icon: '⚠️',
      }
    );
  }, [sessionTimeout, onSessionWarning, handleSessionExpired]);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // If there was a warning shown, dismiss it and reset the flag
    if (warningShownRef.current) {
      toast.dismiss();
      warningShownRef.current = false;
      console.log('✅ [SESSION] Session extended by user activity - warning dismissed');
    }
    
    // Reset activity timeout
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    
    // Set new activity timeout for warning
    activityTimeoutRef.current = setTimeout(() => {
      handleSessionWarning();
    }, activityTimeout * 60 * 1000);
    
    // Set new session timeout for logout
    sessionTimeoutRef.current = setTimeout(() => {
      handleSessionExpired();
    }, sessionTimeout * 60 * 1000);
  }, [activityTimeout, sessionTimeout, handleSessionWarning, handleSessionExpired]);

  // Check if token needs refresh
  const checkTokenExpiry = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const tokenData = parseJwtToken(token);
    if (!tokenData) return;

    const now = Date.now();
    const timeUntilExpiry = tokenData.exp - now;
    const refreshTime = refreshThreshold * 60 * 1000; // Convert to milliseconds
    const tokenAge = now - tokenData.iat; // How long since token was issued

    // Don't show warnings for very fresh tokens (less than 5 minutes old)
    if (tokenAge < 5 * 60 * 1000) {
      console.log('🔄 [TOKEN REFRESH] Fresh token detected, skipping early expiry checks');
      return;
    }

    // If token expires in less than threshold time, refresh it
    if (timeUntilExpiry <= refreshTime && timeUntilExpiry > 0) {
      console.log(`⏰ [TOKEN REFRESH] Token expires in ${Math.ceil(timeUntilExpiry / 60000)} minutes, refreshing...`);
      await refreshToken();
    } else if (timeUntilExpiry <= 0) {
      console.warn('⚠️ [TOKEN REFRESH] Token already expired');
      handleSessionExpired();
    }
  }, [parseJwtToken, refreshThreshold, refreshToken, handleSessionExpired]);

  // Initialize session management and clear any existing warnings
  useEffect(() => {
    // Clear any existing session warnings from previous sessions
    toast.dismiss();
    warningShownRef.current = false;
    
    console.log('🔄 [SESSION] Authentication session manager initialized');
  }, []);

  // Activity event listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const throttledUpdateActivity = (() => {
      let lastCall = 0;
      return () => {
        const now = Date.now();
        if (now - lastCall >= 60000) { // Throttle to once per minute
          lastCall = now;
          updateActivity();
        }
      };
    })();

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, true);
    });

    // Initial activity update (with a small delay to ensure clean initialization)
    const initTimeout = setTimeout(() => {
      updateActivity();
    }, 1000);

    return () => {
      // Remove event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity, true);
      });
      clearTimeout(initTimeout);
    };
  }, [updateActivity]);

  // Token refresh interval
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    // Check token expiry every minute
    refreshIntervalRef.current = setInterval(checkTokenExpiry, 60 * 1000);

    // Initial check
    checkTokenExpiry();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [checkTokenExpiry]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    };
  }, []);

  // Manual function to dismiss session warnings
  const dismissSessionWarning = useCallback(() => {
    if (warningShownRef.current) {
      toast.dismiss();
      warningShownRef.current = false;
      console.log('✅ [SESSION] Session warning manually dismissed');
    }
  }, []);

  return {
    refreshToken,
    updateActivity,
    dismissSessionWarning,
    lastActivity: lastActivityRef.current
  };
}; 